/* eslint-disable no-underscore-dangle */
import React, { useMemo, useCallback } from 'react'
import { useSelector } from 'react-redux'
import { ColDef, GetContextMenuItems } from 'ag-grid-enterprise'
import { FinancialStatementRowAction, StatementColumn } from '../types'
import { contextCompanySelector } from '../../../redux/context/company/selectors'
import { EnhancedStatementRow } from '../utils/dataEnhancement'
import ValueCellRenderer from '../components/StatementTable/cellRenderers/ValueCellRenderer'
import { ReportDataType } from '../../../redux/context/reports/types'
import {
  createAutoGroupColumnDef,
  createHiddenExportColumns,
  createContextMenuItems,
  getCommonColumnProps,
  getBaseCellClasses
} from '../utils/columnUtils'
import { useAgGridLocale } from '../../../shared/hooks/useAgGridLocale'

interface UseBalanceSheetColumnsOptions {
  columns: StatementColumn[]
  onShowVouchers?: (row: EnhancedStatementRow, column: StatementColumn) => void
  // onRowAction?: (rowData: EnhancedStatementRow, action: FinancialStatementRowAction) => void
  dataTypes?: ReportDataType[]
}

interface UseBalanceSheetColumnsReturn {
  columnDefs: ColDef<EnhancedStatementRow>[]
  autoGroupColumnDef: ColDef<EnhancedStatementRow>
  getContextMenuItems: GetContextMenuItems<EnhancedStatementRow>
}

export interface StatementColumnContext {
  column?: StatementColumn
}

export interface StatementColDef extends ColDef<EnhancedStatementRow, any> {
  context?: StatementColumnContext
}

export const useBalanceSheetColumns = ({
  columns,
  onShowVouchers,
  dataTypes
}: UseBalanceSheetColumnsOptions): UseBalanceSheetColumnsReturn => {
  const localeText = useAgGridLocale()
  const accountingSoftware = useSelector(contextCompanySelector)?.accountingSoftware
  const getContextMenuItemsCallback = useMemo(
    () => createContextMenuItems(onShowVouchers, undefined, accountingSoftware),
    [onShowVouchers, accountingSoftware]
  )

  // Auto group column definition for tree data
  const autoGroupColumnDef = useMemo(
    (): StatementColDef =>
      createAutoGroupColumnDef({
        localeText
      }),
    [localeText]
  )

  // Helper function to create a column definition for a single column
  const createColumnDef = useCallback(
    (column: StatementColumn): StatementColDef => {
      return {
        ...getCommonColumnProps(column),
        cellRenderer: ValueCellRenderer,
        cellRendererParams: {
          showPercentage: false
        },
        cellClass: params => getBaseCellClasses(params, column, accountingSoftware)
      }
    },
    [dataTypes, accountingSoftware]
  )

  // Memoized column definitions - simple flat columns for balance sheet
  const columnDefs = useMemo((): ColDef<EnhancedStatementRow>[] => {
    const cols: ColDef<EnhancedStatementRow>[] = []

    // Add hidden export columns (account code and dimension)
    cols.push(...createHiddenExportColumns())

    // Add data columns - flat structure for balance sheet (no month grouping)
    columns?.forEach((column: StatementColumn) => {
      cols.push(createColumnDef(column))
    })

    return cols
  }, [columns, createColumnDef])

  return {
    columnDefs,
    autoGroupColumnDef,
    getContextMenuItems: getContextMenuItemsCallback
  }
}
